import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Pencil, Settings, Image as ImageIcon, Grid3x3, Star, Layout, X, Zap } from "lucide-react";
import HeroInlineEditor from "@/components/HeroInlineEditor";

/**
 * Overlay visible only to admins on the public home page.
 * Toggles an "edit mode" that surfaces contextual edit buttons on each editable section.
 * Hero is edited inline via a side drawer; other sections deep-link to /admin/home-editor.
 */
export default function HomeAdminOverlay() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [editMode, setEditMode] = useState(params.get("edit") === "1");
  const [heroEditorOpen, setHeroEditorOpen] = useState(false);

  useEffect(() => {
    if (params.get("edit") === "1") setEditMode(true);
  }, [params]);

  useEffect(() => {
    if (!editMode) return;
    document.body.classList.add("admin-edit-mode");
    return () => document.body.classList.remove("admin-edit-mode");
  }, [editMode]);

  if (!user?.is_admin) return null;

  const openHero = (e) => {
    e?.preventDefault?.();
    setHeroEditorOpen(true);
  };

  return (
    <>
      {/* Floating toggle */}
      <div
        data-testid="home-admin-toolbar"
        className="fixed bottom-24 lg:bottom-28 right-6 z-[60] flex flex-col items-end gap-3"
      >
        {editMode && !heroEditorOpen && (
          <div className="light-panel bg-white text-black shadow-2xl border border-black/10 p-3 flex flex-col gap-1 min-w-[240px] [&_a]:text-black [&_button]:text-black" data-testid="home-admin-menu">
            <div className="text-[10px] uppercase tracking-widest text-black/50 px-2 pb-1.5 mb-1 border-b border-black/10 flex items-center gap-2">
              <Settings className="w-3 h-3 text-[#22ff66]" /> Editor rapido home
            </div>
            <button
              onClick={openHero}
              data-testid="admin-edit-hero"
              className="flex items-center gap-2 text-sm hover:bg-[#22ff66]/10 hover:text-[#22ff66] px-3 py-2 transition text-left"
            >
              <Zap className="w-4 h-4 text-[#22ff66]" /> Modifica Hero <span className="ml-auto text-[9px] uppercase tracking-widest text-black/40">Inline</span>
            </button>
            <Link
              to="/admin/home-editor?tab=cat"
              data-testid="admin-edit-cat"
              className="flex items-center gap-2 text-sm hover:bg-[#22ff66]/10 hover:text-[#22ff66] px-3 py-2 transition"
            >
              <Grid3x3 className="w-4 h-4" /> Categorie
            </Link>
            <Link
              to="/admin/home-editor?tab=tst"
              data-testid="admin-edit-tst"
              className="flex items-center gap-2 text-sm hover:bg-[#22ff66]/10 hover:text-[#22ff66] px-3 py-2 transition"
            >
              <Star className="w-4 h-4" /> Testimonianze
            </Link>
            <Link
              to="/admin/inventario"
              data-testid="admin-edit-fleet"
              className="flex items-center gap-2 text-sm hover:bg-[#22ff66]/10 hover:text-[#22ff66] px-3 py-2 transition"
            >
              <Layout className="w-4 h-4" /> Flotta
            </Link>
          </div>
        )}
        <button
          data-testid="home-admin-toggle"
          onClick={() => setEditMode((v) => !v)}
          className={`w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all ${
            editMode ? "bg-black text-white rotate-45" : "bg-[#22ff66] text-black hover:bg-[#4EFF87] hover:scale-105"
          }`}
          title={editMode ? "Chiudi editor" : "Modifica Home"}
          aria-label={editMode ? "Chiudi editor" : "Modifica Home"}
        >
          {editMode ? <X className="w-6 h-6" /> : <Pencil className="w-5 h-5" />}
        </button>
      </div>

      {/* Section-level edit chips shown only in edit mode */}
      {editMode && <SectionEditChips onEditHero={openHero} />}

      {/* Inline hero drawer */}
      <HeroInlineEditor open={heroEditorOpen} onClose={() => setHeroEditorOpen(false)} />
    </>
  );
}

function SectionEditChips({ onEditHero }) {
  return (
    <>
      <style>{`
        .admin-edit-mode [data-testid=hero-section],
        .admin-edit-mode [data-testid=rental-booking-banner],
        .admin-edit-mode [data-testid=fleet-carousel] {
          outline: 2px dashed rgba(34,255,102,0.6);
          outline-offset: -6px;
          position: relative;
        }
        .admin-edit-mode [data-testid=rental-booking-banner]::after,
        .admin-edit-mode [data-testid=fleet-carousel]::after {
          content: "Modifica sezione →";
          position: absolute;
          top: 10px;
          right: 12px;
          background: #22ff66;
          color: #fff;
          font-size: 10px;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          padding: 6px 10px;
          z-index: 5;
          pointer-events: none;
        }
      `}</style>
      {/* Hero has an actual button because we need the click handler */}
      <HeroEditButton onClick={onEditHero} />
    </>
  );
}

function HeroEditButton({ onClick }) {
  const [top, setTop] = useState(90);
  useEffect(() => {
    const compute = () => {
      const el = document.querySelector('[data-testid=hero-section]');
      if (!el) return;
      const r = el.getBoundingClientRect();
      setTop(Math.max(90, r.top + 20));
    };
    compute();
    window.addEventListener("scroll", compute);
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute);
      window.removeEventListener("resize", compute);
    };
  }, []);
  return (
    <button
      data-testid="edit-hero-chip"
      onClick={onClick}
      style={{ top: `${top}px` }}
      className="fixed right-6 z-[55] bg-[#22ff66] hover:bg-[#4EFF87] text-black text-[10px] uppercase tracking-widest font-bold px-3 h-8 flex items-center gap-1.5 shadow-lg"
    >
      <Pencil className="w-3 h-3" /> Modifica Hero
    </button>
  );
}
